const merge = require('deepmerge');

const initialState = {
  authToken: localStorage.getItem('authToken') || null,
  clientVersion: '1.6.0'
}

// Custom merge strategy for arrays - always use the source array
const overwriteMerge = (destinationArray, sourceArray) => sourceArray;

// Partial quote updates merge line rows by id instead of replacing the whole array.
function quoteLineIdsMatch(a, b) {
  return a != null && b != null && String(a) === String(b);
}

function applyQuoteLineIdRemapToLines(lines, idRemap) {
  if (!Array.isArray(lines) || !idRemap || !Object.keys(idRemap).length) return lines;
  return lines.map((line) => {
    if (!line || line.id == null) return line;
    const realId = idRemap[String(line.id)];
    return realId != null ? { ...line, id: realId } : line;
  });
}

function mergeQuoteLinesById(targetLines, sourceLines) {
  if (!Array.isArray(sourceLines)) return targetLines;
  // Explicit empty array from API (e.g. create quote without lines) — drop template rows.
  if (sourceLines.length === 0) return [];
  let result = Array.isArray(targetLines) ? [...targetLines] : [];
  sourceLines.forEach((line) => {
    if (line == null) return;
    if (line._destroy) {
      result = result.filter((l) => !quoteLineIdsMatch(l?.id, line.id));
      return;
    }
    const ix = result.findIndex((l) => quoteLineIdsMatch(l?.id, line.id));
    if (ix >= 0) {
      result[ix] = merge(result[ix], line, mergeOptions);
      return;
    }
    const orphanIx = result.findIndex((l) => l?.id == null);
    if (orphanIx >= 0 && result.filter((l) => l?.id == null).length === 1) {
      result[orphanIx] = merge(result[orphanIx], line, mergeOptions);
      return;
    }
    result.push(line);
  });
  return result;
}

// Merge options for deepmerge
const mergeOptions = { arrayMerge: overwriteMerge };

// Whether a stored user falls inside a fetchUsers scope ({ type, customerId }),
// so a fetch replaces exactly the users it covers.
function userInScope(user, { type, customerId }) {
  if (type === 'staff') return user.type === 'staff';
  if (type === 'operators') {
    return user.type === 'operator' && (customerId == null || `${user.customer?.id}` === `${customerId}`);
  }
  return true;
}

// Keeps each template's assigned_to list in step with a saved operator.
function syncTemplateAssignment(templates, user) {
  if (!templates || user.type !== 'operator') return templates;
  return Object.fromEntries(Object.entries(templates).map(([id, template]) => {
    const others = (template.assigned_to || []).filter((a) => a.id !== user.id);
    const assigned_to = `${template.id}` === `${user.report_template_id}`
      ? [...others, { id: user.id, name: user.name, deactivated: !!user.deactivated_at }]
      : others;
    return [id, { ...template, assigned_to }];
  }));
}

const byId = (records) => Object.fromEntries(records.map((r) => [r.id, r]));

const quoteMergeOptions = {
  arrayMerge: overwriteMerge,
  customMerge: (key) => {
    if (key === 'quote_lines') {
      return mergeQuoteLinesById;
    }
  },
};

function todayLogDate() {
  return new Date().toLocaleString().split(',')[0];
}

function mergeServiceLogNode(logs, node) {
  if (!node?.id) return logs || {};

  const next = { ...(logs || {}) };
  const nodeId = String(node.id);
  const existingDate = Object.keys(next).find((date) =>
    Object.keys(next[date] || {}).some((key) => String(key) === nodeId)
  );
  const targetDate = existingDate || node.logDate || todayLogDate();
  const day = { ...(next[targetDate] || {}) };

  if (node.deleted) {
    delete day[node.id];
    delete day[nodeId];
  } else {
    const current = day[node.id] || day[nodeId] || {};
    day[node.id] = { ...current, ...node, synced: false };
    if (nodeId !== String(node.id)) delete day[nodeId];
  }

  if (Object.keys(day).length === 0) {
    delete next[targetDate];
  } else {
    next[targetDate] = day;
  }

  return next;
}

// Puts the server's calculated tests into the report:
// calculated = { resultId: { component_id, result, calc } }. Only the result
// and what it was worked out from are replaced; values the user is typing
// into a calculated test's inputs are left alone.
function applyCalculatedResults(report, calculated) {
  const entries = Object.entries(calculated || {});
  if (!report?.components || entries.length === 0) return report;

  const components = {...report.components};
  entries.forEach(([resultId, payload]) => {
    const component = components[payload.component_id];
    const test = component?.tests?.[resultId];
    if (!test) return;

    components[payload.component_id] = {
      ...component,
      tests: {
        ...component.tests,
        [resultId]: {...test, type: 'calculated', result: payload.result, calc: payload.calc}
      }
    };
  });

  return {...report, components};
}

function rootReducer(state = initialState, action) {
  switch (action.type) {
    case 'INITIAL_LOAD':
      return {
        ...state,
        initialLoadComplete: action.state
      }
    case 'FETCH_BEGIN':
      return {
        ...state,
        loading: true,
        loadingMessage: action.message,
        // error: null
      };
    case 'FETCH_SUCCESS':
      // console.log('fetch completed with response', action.response);
      return {
        ...state,
        loading: false, loadingMessage: null,
      }
    case 'FETCH_FAILURE':
      console.log('fetch failure', action.payload);
      return {
        ...state,
        loading: false, loadingMessage: null,
        error: action.payload.message || action.payload.statusText || action.payload,
        loadingMessage: null
      };
    case 'FETCH_SESSION_SUCCESS':
      localStorage.setItem('user', action.payload.user.username);
      localStorage.setItem('authToken', action.payload.user.token);

      return {
        ...state,
        loading: false, loadingMessage: null,
        authToken: action.payload.user.token,
        user: {
          id: action.payload.user.id,
          username: action.payload.user.username,
          name: action.payload.user.name,
          admin: action.payload.user.admin,
          employee: action.payload.user.employee,
          operator: action.payload.user.operator,
          managesOperators: action.payload.user.manages_operators,
          email: action.payload.user.email,
          tempPass: action.payload.user.temppass,
          alert: action.payload.user.alert,
          sessionCreated: Date.now(),
          managing: action.payload.user.managing,
          reports_color_scheme: action.payload.user.reports_color_scheme,
          lookup_color_scheme: action.payload.user.lookup_color_scheme,
          schedule_color_scheme: action.payload.user.schedule_color_scheme,
          map_color_scheme: action.payload.user.map_color_scheme,
          orders_color_scheme: action.payload.user.orders_color_scheme
        },
        debug: action.payload.user.debug,
        lastLogged: action.payload.user.username,
      };
    case 'UPDATE_COLOR_SCHEME':
      return {
        ...state,
        user: {
          ...state.user,
          ...action.payload
        }
      }
    case 'DEBUG_SESSION':
      console.log('debugging session', action.newToken);
      localStorage.setItem('authToken', action.newToken);
      return {
        ...state,
        authToken: action.newToken
      }
    case 'FETCH_ACCOUNTS_SUCCESS':
      return {
        ...state,
        accounts: action.payload.customers,
        customers: action.payload.customers,
        loading: false, loadingMessage: null
      };
    case 'FETCH_REPORT_SUCCESS':

      // // load existing report from localStorage
      // let localReport = localStorage.getItem(`report-${action.report.hash_id}`);
      //
      // if (localReport) {
      //   localReport = JSON.parse(localReport);
      //   localReport['local'] = true;
      //
      //   action.report = action.report.last_edit > localReport?.last_edit ? action.report : localReport;
      // };

      return {
        ...state,
        loading: false, loadingMessage: null,
        report: action.report
      }
    case 'FETCH_REPORT_DRAFTS_SUCCESS':
      // console.log('found', action.drafts.length, 'report drafts');
      return {
        ...state,
        loading: false, loadingMessage: null,
        drafts: action.drafts
      }
    case 'TERM_SEARCHED':
      return {
        ...state,
        pastSearches: (state.pastSearches || []).concat(action.term).pop() //yeah i said it fuck you
      }
    case 'FETCH_INFO_SUCCESS':
      let searched = {};
      if (action.info.model) {
        searched = {name: action.info.model, id: action.info.id, type: 'p'};
      } else if (action.info.name) {
        searched = {name: action.info.name, id: action.info.id, type: 'a'};
      }

      let pastSearches = state.pastSearches;
      //check if searched is valid and pastSearches doesnt contain searched
      if (searched.name && !(state.pastSearches || []).map(s => s.name)?.includes(searched?.name)) { //woah homie calm down
        //pop first items from pastSearches if length over 5
        if (pastSearches?.length > 3) {
          pastSearches = pastSearches.slice(pastSearches.length-3, pastSearches.length);
        }
        pastSearches = (pastSearches || []).concat(searched)
      }

      return {
        ...state,
        info: action.info,
        loading: false, loadingMessage: null,
        pastSearches: pastSearches
      };
    case 'UPDATE_REPORT_TEMPLATE':
      // called when edit has been made to report template via lookup
      return {
        ...state,
        loading: false, loadingMessage: null,
        info: {
          ...state.info,
          reportTemplate: action.payload
        }
      };
    case 'UPDATE_CONTACT_SUCCESS':
      return {
        ...state,
        loading: false, loadingMessage: null,
        info: {
          ...state.info,
          contacts: action.payload.contacts
        }
      };
    case 'UPDATE_SERVICE_LOG_LOCAL':
      return {
        ...state,
        info: {
          ...state.info,
          serviceLog: mergeServiceLogNode(state.info?.serviceLog, action.payload)
        }
      };
    case 'UPDATE_SERVICE_LOG_SUCCESS':
      return {
        ...state,
        loading: false, loadingMessage: null,
        info: {
          ...state.info,
          serviceLog: action.payload.serviceLog
        }
      };
    case 'UPDATE_WASTEWATER_PRICE_SUCCESS':
      return {
        ...state,
        loading: false, loadingMessage: null,
        info: {
          ...state.info,
          wastewater_prices: action.payload.wastewater_prices
        }
      };
    case 'UPDATE_PRODUCT_SDS_SUCCESS':
      return {
        ...state,
        loading: false, loadingMessage: null,
        info: {
          ...state.info,
          sds_link: action.payload.sds_link
        }
      };
    case 'USERS_LOADED': {
      const { users, employees, positions, scope } = action.payload;
      const kept = Object.values(state.users || {}).filter((u) => !userInScope(u, scope));
      return {
        ...state,
        users: byId([...kept, ...users]),
        ...(employees ? { employees } : {}),
        ...(positions ? { employeePositions: positions } : {})
      };
    }
    case 'USER_SAVED':
      return {
        ...state,
        users: { ...state.users, [action.payload.id]: action.payload },
        reportTemplates: syncTemplateAssignment(state.reportTemplates, action.payload)
      };
    case 'REPORT_TEMPLATES_LOADED': {
      const { customerId, templates, catalog } = action.payload;
      const kept = Object.values(state.reportTemplates || {}).filter((t) => `${t.customer_id}` !== `${customerId}`);
      return {
        ...state,
        reportTemplates: byId([...kept, ...templates]),
        ...(catalog ? { templateCatalogs: { ...state.templateCatalogs, [customerId]: catalog } } : {})
      };
    }
    case 'CLEAR_INFO':
      return {
        ...state,
        info: null
      };
    case 'CLEAR_SESSION':
      // console.log('setting last logged', state.lastLogged);
      localStorage.clear();

      return {
        // ...state,
        loading: false, loadingMessage: null,
        authToken: null,
        user: null,
        quoteDrafts: null,
        quoteDraftsMeta: null,
        initialLoadComplete: false,
        error: action.message,
        lastLogged: state.lastLogged,
        drafts: state.drafts,
        clientVersion: state.clientVersion
      };
    case 'UPDATE_REPORT':
      // Deep merge the report with the new data
      const updatedReport = merge(state.report, action.payload, mergeOptions);

      // Persist to localStorage if needed
      if (action.local) {

        // if adding the report would exceed 5MB, clean up localStorage

        // Calculate the size of localStorage in kilobytes
        var lsTotal = 0,
            _xLen, _x;
        for (_x in localStorage) {
            if (!localStorage.hasOwnProperty(_x)) {
                continue;
            }
            _xLen = ((localStorage[_x].length + _x.length) * 2);
            lsTotal += _xLen;
            console.log(_x.substr(0, 50) + " = " + (_xLen / 1024).toFixed(2) + " KB")
        };
        console.log("Total = " + (lsTotal / 1024).toFixed(2) + " KB");

        const updatedReportSize = (updatedReport.length + `report-${updatedReport.hash_id}`.length) * 2;

        if (lsTotal + updatedReportSize > 1024 * 1024 * 5) {
          // Clean up localStorage when reaching 5MB limit
          // Keep only 'user', 'authToken', and keys starting with 'report-'
          Object.keys(localStorage).forEach(key => {
            if (key !== 'user' && key !== 'authToken') {
              localStorage.removeItem(key);
              console.log(`Removed localStorage key: ${key}`);
            }
          });
          console.log("localStorage cleanup completed due to 5MB limit");
        }
       
        // A report holding a large attachment (a PDF, a big photo) can be over
        // the browser's storage quota. The local copy is only a backup, so a
        // failed write must not stop the update from reaching the API.
        try {
          localStorage.setItem(`report-${updatedReport.hash_id}`, JSON.stringify(updatedReport));
        } catch (error) {
          console.warn('Could not store the report locally', error);
        }
      }

      // Update drafts, removing the active report draft and adding the freshly updated one
      const updatedDrafts = [
        ...(state.drafts || []).filter(draft => draft.id !== state.report.id),
        updatedReport
      ];

      const setLoading = action.freeze ? true : false;

      return {
        ...state,
        loading: setLoading,
        loadingMessage: null,
        report: updatedReport,
        drafts: updatedDrafts
      };
    case 'CONFIRM_REPORT_UPDATE':
      // example breadcrumb: 385138.components.7891.tests.1744294127901
      // basically when the reoprt is sent to api for updates we're adding {...saving: true}, and removing it when the breadcrumb trail comes back
      // that way components can know when the report has been updated
      let reportCopy = {...state.report};
      let breadcrumbs = action.payload.breadcrumbs;
      let idRemap = action.payload.id_remap || {};
      console.log('breadcrumbs', breadcrumbs, 'id_remap', idRemap);

      // Apply id_remap first so any breadcrumbs that reference the new real
      // component id can find a matching entry. The server returns a mapping
      // like { "-1001": 92366 } when components created this round-trip
      // (those originally sent under a temp negative id) get persisted.
      if (Object.keys(idRemap).length > 0) {
        reportCopy.components = {...reportCopy.components};
        Object.entries(idRemap).forEach(([tempId, realId]) => {
          const tempKey = String(tempId);
          const realKey = String(realId);
          const existing = reportCopy.components[tempKey];
          if (!existing) return;
          reportCopy.components[realKey] = {
            ...existing,
            id: typeof existing.id === 'number' ? Number(realId) : realId,
            synced: true,
          };
          delete reportCopy.components[tempKey];
        });
      }

      breadcrumbs.forEach(crumbtrail => {
        try {
          if (typeof crumbtrail !== 'string') return;
          let crumbs = crumbtrail.split('.');
          // crumbs[0] is always a string (split result); reportCopy.id comes
          // from Rails' as_json as a number. Compare as strings so the walker
          // doesn't bail out and leave `synced: false` on every patched node.
          if (`${crumbs[0]}` !== `${reportCopy.id}`) {
            return;
          } else {
            crumbs.shift();
          }

          // Create new references at each level for immutability
          let current = reportCopy;
          for (let i = 0; i < crumbs.length - 1; i++) {
            const crumb = crumbs[i];
            current[crumb] = {...current[crumb]};
            current = current[crumb];
          }

          // Update the final target with a new reference
          const lastCrumb = crumbs[crumbs.length - 1];
          if (current[lastCrumb]) {
            current[lastCrumb] = {...current[lastCrumb], synced: true};
            console.log('breadcrumb target found', current[lastCrumb]);
          }
        } catch (error) {
          throw new Error(`error confirming report save, ${error}, breadcrumbs: ${breadcrumbs}`);
        }
      });

      // When a node is added, the server may number the component's
      // unpositioned nodes so the new one lands last; mirror those positions.
      Object.entries(action.payload.positions || {}).forEach(([componentId, kinds]) => {
        const component = reportCopy.components?.[componentId];
        if (!component) return;
        const nextComponent = {...component};
        Object.entries(kinds || {}).forEach(([kind, byId]) => {
          if (!nextComponent[kind]) return;
          nextComponent[kind] = {...nextComponent[kind]};
          Object.entries(byId || {}).forEach(([nodeId, position]) => {
            if (nextComponent[kind][nodeId]) {
              nextComponent[kind][nodeId] = {...nextComponent[kind][nodeId], position};
            }
          });
        });
        reportCopy.components = {...reportCopy.components, [componentId]: nextComponent};
      });

      console.log('confirmed update', reportCopy);

      return {
        ...state,
        loading: false, loadingMessage: null,
        report: applyCalculatedResults(reportCopy, action.payload.calculated)
      }
    case 'CALCULATED_RESULTS':
      return {
        ...state,
        report: applyCalculatedResults(state.report, action.payload)
      }
    case 'LOAD_TEST_HISTORY':
      let history = (state.testHistory || []).filter(node => !(node.component === action.component && node.test == action.test));
      let newHistoryNode = {
        component: action.component,
        test: action.test,
        history: action.history
      };
      history.push(newHistoryNode)
      return {
        ...state,
        testHistory: history,
        loading: false, loadingMessage: null,
        // error: null,
      }
    case 'EXIT_REPORT':
      localStorage.removeItem(`report-${state.report.hash_id}`);
      return {
        ...state,
        drafts: (state.drafts || []).filter(r => r.id !== state.report.id).concat(state.report),
        loading: false, loadingMessage: null,
        report: null,
        testHistory: null
      };
    case 'REPORT_SUBMITTED':
      return {
        ...state,
        loading: false, loadingMessage: null,
        // drafts: [...state.drafts.filter(r => r.id !== state.report.id)],
        // reportHistory: [...state.reportHistory, state.report]
      };
    case 'DELETE_REPORT_SUCCESS':
      // console.log('deleting report', action.payload);
      return {
        ...state,
        info: {
          ...(state.info || {}),
          reports: (state.info?.reports || []).filter(r => r.id !== action.payload)
        },
        drafts: (state.drafts || []).filter(r => r.id !== action.payload),
        loading: false, loadingMessage: null,
        report: null
      };
    case 'FETCH_CALENDAR_SUCCESS':
      // console.log('finished calendar fetch');
      return {
        ...state,
        loading: false, loadingMessage: null,
        serviceableAccounts: action.payload.serviceableAccounts,
        managedAccounts: action.payload.managedAccounts,
        events: action.payload.events
      };
    case 'NEW_EVENT':
      return {
        ...state,
        loading: false, loadingMessage: null,
        events: (state.events || []).concat(action.payload)
      };
    case 'UPDATE_EVENT':
      // console.log('CAUGHT THE BALL', action.payload);
      return {
        ...state,
        loading: false, loadingMessage: null,
        events: state.events.filter(e => e.id !== action.payload.id).concat(action.payload)
      };
    case 'DESTROY_EVENT':
      // console.log('triggered destroy reducer');
      return {
        ...state,
        loading: false, loadingMessage: null,
        events: state.events.filter(e => e.id !== action.eventId)
      }
    case 'FETCH_PRODUCTS_SUCCESS':
      return {
        ...state,
        loading: false, loadingMessage: null,
        products: action.payload.products,
        vendors: action.payload.vendors,
      };
    case 'FETCH_REPORTS_LIST_SUCCESS':
      // console.log('reports list success', action.payload);
      return {
        ...state,
        loading: false, loadingMessage: null,
        reports: action.payload.reports
      };
    case 'FETCH_SCHEDULED_SERVICE_LIST_SUCCESS':
      // console.log('scheduled service list success', action.payload);
      return {
        ...state,
        loading: false, loadingMessage: null,
        scheduledService: action.payload.scheduled_service
      };
    case 'FETCH_RECENTLY_SEARCHED_SUCCESS':
      // console.log('recent searches fetch success', action.payload);
      return {
        ...state,
        loading: false, loadingMessage: null,
        recentlySearched: action.payload.recently_searched
      };
    case 'UPDATE_ORDER':
      // console.log('saving order', action.order);
      return {
        ...state,
        loading: false, loadingMessage: null, 
        order: action.order
      };
    case 'OFFLINE_REPORT_DATA':
      return {
        ...state,
        offlineTemplates: action.reports,
        loading: false, loadingMessage: null,
      }
    case 'OFFLINE_REPORT_SUBMIT':
      return {
        ...state,
        offlineSubmitted: state.offlineSubmitted.push(action.payload),
        loading: false, loadingMessage: null,
      }
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      }
    case 'TEST_ERROR':
      return {
        ...state,
        error: 'egg'
      }
    case 'REPORT_HISTORY': {
      const incoming = Array.isArray(action.reports) ? action.reports : [];
      const reportHistory = action.append && Array.isArray(state.reportHistory)
        ? [...state.reportHistory, ...incoming]
        : incoming;

      return {
        ...state,
        loading: false,
        loadingMessage: null,
        reportHistory,
        reportHistoryMeta: {
          total_count: action.total_count ?? reportHistory.length,
          has_more: action.has_more ?? false,
        },
      }
    }
    case 'ORDER_HISTORY': {
      let orders = action.orders;
      if (typeof orders === 'string') {
        try { orders = JSON.parse(orders); } catch { orders = []; }
      }
      if (!Array.isArray(orders)) orders = [];
      const merged = action.append && Array.isArray(state.orderHistory)
        ? [...state.orderHistory, ...orders]
        : orders;
      return {
        ...state,
        loading: false,
        loadingMessage: null,
        orderHistory: merged
      };
    }
    case 'FETCH_ORDER_PRODUCTS_SUCCESS':
      return {
        ...state,
        loading: false,
        loadingMessage: null,
        orderProducts: action.products
      };
    case 'FETCH_ORDER_DELIVERY_OPTIONS_SUCCESS':
      return {
        ...state,
        orderDeliveryOptions: action.options
      };
    case 'ORDER_DRAFTS': {
      let orders = action.orders;
      if (typeof orders === 'string') {
        try { orders = JSON.parse(orders); } catch { orders = []; }
      }
      if (!Array.isArray(orders)) orders = [];
      return {
        ...state,
        loading: false,
        loadingMessage: null,
        orderDrafts: orders,
      };
    }
    case 'LOAD_ORDER_DRAFT_SUCCESS': {
      let order = action.order;
      if (typeof order === 'string') {
        try { order = JSON.parse(order); } catch { order = null; }
      }
      return {
        ...state,
        loading: false,
        loadingMessage: null,
        activeOrderDraft: order,
      };
    }
    case 'CLEAR_ACTIVE_ORDER_DRAFT':
      return {
        ...state,
        activeOrderDraft: null,
      };
    case 'DELETE_ORDER_DRAFT_SUCCESS': {
      const deletedId = action.id;
      const drafts = (state.orderDrafts || []).filter((o) => o.id !== deletedId);
      return {
        ...state,
        loading: false,
        loadingMessage: null,
        orderDrafts: drafts,
        activeOrderDraft: state.activeOrderDraft?.id === deletedId ? null : state.activeOrderDraft,
      };
    }
    case 'SAVE_ORDER_SUCCESS': {
      let order = action.order;
      if (typeof order === 'string') {
        try { order = JSON.parse(order); } catch { order = null; }
      }
      const isSubmitted = !!order?.submitted;
      const silent = !!action.silent;
      let history = Array.isArray(state.orderHistory) ? [...state.orderHistory] : [];
      let drafts = Array.isArray(state.orderDrafts) ? [...state.orderDrafts] : [];

      if (isSubmitted) {
        drafts = drafts.filter((o) => o.id !== order.id);
        const histIx = history.findIndex((o) => o.id === order.id);
        if (histIx >= 0) history[histIx] = order;
        else history.unshift(order);
        return {
          ...state,
          ...(silent ? {} : { loading: false, loadingMessage: null }),
          lastSubmittedOrder: order,
          orderHistory: history,
          orderDrafts: drafts,
          activeOrderDraft: null,
        };
      }

      const draftIx = drafts.findIndex((o) => o.id === order?.id);
      if (draftIx >= 0) drafts[draftIx] = order;
      else if (order?.id) drafts.unshift(order);

      return {
        ...state,
        ...(silent ? {} : { loading: false, loadingMessage: null }),
        orderDrafts: drafts,
        // Silent autosave must not clobber the open wizard by resetting activeOrderDraft
        activeOrderDraft: silent ? state.activeOrderDraft : order,
      };
    }
    case 'TEST_FIELDS':
      // console.log('tests received:', action.payload);
      return {
        ...state,
        loading: false, loadingMessage: null, 
        testFields: action.payload
      }
    case 'NAV_HEADER':
      return {
        ...state,
        navHeader: action.payload
      }
    case 'SHOW_NAV':
      return {
        ...state, 
        showNav: action.payload
      }
    case 'UPDATE_LOOKUP_INFO':
      return {
        ...state,
        loading: false, loadingMessage: null,
        info: action.payload
      }
    case 'INITIALIZE_QUOTES_SUCCESS': {
      const drafts = action.payload.drafts || [];
      const meta = action.payload.drafts_meta || {
        total_count: drafts.length,
        page: 1,
        per_page: 10,
        total_pages: 1
      };
      return {
        ...state,
        loading: false, loadingMessage: null,
        quoteDrafts: drafts,
        quoteDraftsMeta: meta,
        quoteDraftsList: 'drafts',
        quotableProducts: action.payload.quotable_products,
        activeQuote: action.payload.active_quote,
        customers: action.payload.customers,
        quoteEmployees: action.payload.employees || []
      };
    }
    case 'QUOTE_DRAFTS_PAGE_SUCCESS': {
      const drafts = action.payload.drafts || [];
      const meta = action.payload.drafts_meta || {
        total_count: drafts.length,
        page: 1,
        per_page: 10,
        total_pages: 1
      };
      return {
        ...state,
        quoteDrafts: drafts,
        quoteDraftsMeta: meta,
        quoteDraftsList: action.payload.drafts_list || state.quoteDraftsList || 'drafts',
      };
    }
    case 'NEW_QUOTE_SUCCESS':
      // console.log('new quote success', action.payload);
      return action.payload.quotable_products ? 
      {
        ...state,
        loading: false, loadingMessage: null,
        activeQuote: action.payload.quote,
        quotableProducts: action.payload.quotable_products
      } : {
        ...state,
        loading: false, loadingMessage: null,
        activeQuote: action.payload.quote
      }
    case 'UPDATE_QUOTE': {
      const q = action.payload;
      const idRemap = action.idRemap || {};
      const remapQuoteLines = (quote) => {
        if (!quote?.quote_lines) return quote;
        return {
          ...quote,
          quote_lines: applyQuoteLineIdRemapToLines(quote.quote_lines, idRemap),
        };
      };
      const drafts = [...(state.quoteDrafts || [])];
      let quoteDrafts = drafts;
      if (q && q.id) {
        const ix = drafts.findIndex((d) => d.id === q.id);
        if (ix >= 0) {
          drafts[ix] = merge(remapQuoteLines(drafts[ix]), q, quoteMergeOptions);
          quoteDrafts = drafts;
        } else {
          quoteDrafts = [merge(remapQuoteLines({}), q, quoteMergeOptions), ...drafts];
        }
      }
      return {
        ...state,
        loading: false, loadingMessage: null,
        activeQuote: merge(remapQuoteLines(state.activeQuote), q, quoteMergeOptions),
        quoteDrafts,
      };
    }
    case 'LOAD_QUOTE_SUCCESS':
      // console.log('loading quote', action.payload);
      return {
        ...state,
        loading: false, loadingMessage: null,
        activeQuote: action.payload.quote,
        quotableProducts: action.payload.quotable_products || state.quotableProducts
      }
    case 'DELETE_QUOTE_SUCCESS': {
      const deletedId = action.payload;
      return {
        ...state,
        loading: false, loadingMessage: null,
        activeQuote: state.activeQuote?.id === deletedId ? null : state.activeQuote,
        quoteDrafts: (state.quoteDrafts || []).filter((q) => q.id !== deletedId),
        ...(state.info
          ? {
              info: {
                ...state.info,
                quotes: (state.info.quotes || []).filter((q) => q.id !== deletedId),
              },
            }
          : {}),
      };
    }
    case 'SUBMIT_QUOTE_SUCCESS':
      // dont want to set activeQuote to null because we want the quoteGenerator component to remain shown to play the success screen
      return {
        ...state,
        loading: false, loadingMessage: null,
      }
    case 'FILE_UPLOAD_SUCCESS':
      return {
        ...state,
        loading: false, loadingMessage: null,
        info: {
          ...state.info,
          files: state.info.files.concat(action.payload)
        }
      } 

    case 'RENAME_FILE_SUCCESS':
      return {
        ...state,
        loading: false, loadingMessage: null,
        info: {
          ...state.info,
          files: state.info.files.map(f => f.id === action.payload.id ? action.payload : f)
        }
      }
    // case 'MAIL_FILE_SUCCESS':
    //   return {
    //     ...state,
    //     loading: false, loadingMessage: null,
    //   }
    case 'DELETE_FILE_SUCCESS':
      return {
        ...state,
        loading: false, loadingMessage: null,
        info: {
          ...state.info,
          files: state.info.files.filter(f => f.id !== action.payload)
        }
      }
    case 'FETCH_MORE_REPORTS_SUCCESS':
      // Combine existing and new reports, remove duplicates, and sort by timestamp (newest first)
      const combinedReports = [...(state.info?.reports || []), ...action.payload.reports];
      const uniqueReports = combinedReports.filter((report, index, self) => 
        index === self.findIndex(r => r.id === report.id)
      );
      const sortedReports = uniqueReports.sort((a, b) => b.timestamp - a.timestamp);
      
      return {
        ...state,
        loading: false, loadingMessage: null,
        info: {
          ...state.info,
          reports: sortedReports,
          hasMoreReports: action.payload.has_more,
          totalReportsCount: action.payload.total_count
        }
      }
    case 'FETCH_MAP_MARKERS_SUCCESS':
      return {
        ...state,
        loading: false, loadingMessage: null,
        markers: action.payload.markers,
        employees: action.payload.employees
      }
    case 'FETCH_SYSTEM_REVIEW_SUCCESS':
      return {
        ...state,
        loading: false, loadingMessage: null,
        info: {
          ...state.info,
          system_review: action.payload
        }
      }
    default:
      return state
  }
}

export default rootReducer;
